import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CustomButton } from '../Button/Button';
import Ref from '../Ref/Ref';
import styles from './SearchModalContent.module.scss';
import { Icon } from '../Icon';
import SearchMobileCard from '../SearchMobileCard/SearchMobileCard';
import { DebounceInput } from 'react-debounce-input';
import useFetchListWithInfiniteScroll from '../../hooks/useFetchListWithInfiniteScroll';
import VideoService from '../../service/VideoService';
import { LoadingSpinner } from '../Icon/icons';
import RecordNotFound from '../../components/_empty_state/RecordNotFound';
import { useTranslation } from 'react-i18next';
import useFetchFoldersList from '../../hooks/useFetchFoldersList';
import SearchMobileFolder from '../SearchMobileCard/SearchMobileFolder';
import SearchComponentWithTags from '../SearchComponentWithTags/SearchComponentWithTags';

const SearchModalContent = ({ onClose, tab = {}, archived = false, folderId = null, selectedFilter, isReview = null }) => {
  const [isSearch, setIsSearch] = useState(true);
  const [value, setValue] = useState('');

  const handleInputChange = e => {
    setValue(e.target.value);

    if (tab?.folders || tab?.team) {
      searchInputFolders.onChange(e.target.value);
      return;
    }

    searchInputVideos.onChange(e.target.value);
  };

  const onInputChange = useCallback(
    value => {
      if (typeof value === 'string') {
        setValue(prevState => ({ ...prevState, query: value }));
        if (tab?.folders || tab?.team) {
          searchInputFolders.onChange(value);
          return;
        }
        searchInputVideos.onChange('searchTerm', value);
      } else {
        setValue(prevState => ({ ...prevState, tags: value }));
        searchInputVideos.onChange(
          'tags',
          value.map(item => item.id)
        );
      }
    },
    [tab]
  );

  const handleClearInput = () => {
    setValue('');
    searchInputVideos.onChange('');
    searchInputFolders.onChange('');
  };

  const handleDisableSearch = () => {
    setIsSearch(false);
  };

  const myRef = useRef(null);

  useEffect(() => {
    if (myRef.current) {
      myRef.current.focus();
    }
  }, [isSearch]);

  const getVideosWithStats = useCallback(async query => {
    query.folder = folderId;
    query.reviewing = isReview;
    query.include_empty = true;
    if (!isReview) {
      query.omitFiledStories = !query.searchTerm;
    }

    if (archived) {
      query.archived = true;
    }

    if (selectedFilter) {
      query.approvalStatus = selectedFilter.TYPE;
    }

    try {
      const response = await VideoService.getVideos(query);

      return new Promise(function (resolve) {
        return resolve(response);
      });
    } catch (err) {
      return new Promise(function (resolve, reject) {
        return reject(err);
      });
    }
  }, []);
  const { t } = useTranslation();

  const {
    infiniteRef,
    list: videos,
    isEmptyList: isVideosEmpty,
    loading,
    moreLoading,
    searchInput: searchInputVideos,
    query,
  } = useFetchListWithInfiniteScroll({ service: getVideosWithStats });

  const {
    list: folders,
    loading: folderLoading,
    loadList: loadFolders,
    query: folderQuery,
    searchInput: searchInputFolders,
  } = useFetchFoldersList({
    archived: archived,
  });

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.modalHeader}>
        <SearchComponentWithTags
          className={styles.searchInputContainer}
          inputClassName={styles.searchInput}
          onBlur={handleDisableSearch}
          placeholder={t('dashboard.searching.placeholder')}
          alwaysShow={true}
          onChange={onInputChange}
          onClose={handleClearInput}
          clearButton={t('dashboard.searching.clear')}
          searchValue={value}
        />
        <div className={styles.controlButtons}>
          <CustomButton variant="link" onClick={onClose} className={styles.closeButton} ariaLabel={t('general.close')}>
            <Icon src="close" className={styles.iconContainer} />
          </CustomButton>
        </div>
      </div>
      <div className={styles.searchList}>
        {(loading || folderLoading) && (
          <div className={styles.spinnerContainer}>
            <LoadingSpinner theme="light" />
          </div>
        )}
        {(!loading || videos.length > 0) && !tab?.folders && !tab?.team && (
          <Ref innerRef={infiniteRef}>
            <ul>
              {videos.map(video => {
                return (
                  <li key={video.id}>
                    <SearchMobileCard video={video} folders={folders} searchInput={value.query} isReview={isReview} />
                  </li>
                );
              })}
            </ul>
          </Ref>
        )}
        {(tab?.folders || tab?.team) && (!folderLoading || folders.length > 0) && (
          <ul>
            {folders
              .filter(({ users }) => (tab?.team ? users?.length : !users?.length))
              .map(folder => (
                <li key={folder.id}>
                  <SearchMobileFolder folder={folder} searchInput={value.query} />
                </li>
              ))}
          </ul>
        )}
        {moreLoading && (
          <div className={styles.spinnerContainer}>
            <LoadingSpinner theme="light" />
          </div>
        )}
        {((!loading && query.searchTerm && videos?.length === 0 && !isVideosEmpty) ||
          (!folderLoading && folderQuery && folders.length === 0)) && <RecordNotFound searchTerm={query.searchTerm} />}
      </div>
    </div>
  );
};

export default SearchModalContent;
